import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BookingConfigService } from '../../../services/booking-config.service';
import { FormService } from '../../../organizations/features/booking/components/form/service';
import { RouteParamService } from '../../../services/route-param.service';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-park-and-fly-advanced-widget',
    templateUrl: './park-and-fly-advanced-widget.component.html',
    styleUrl: './park-and-fly-advanced-widget.component.css',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [FormService, BookingConfigService]
})
export class ParkAndFlyAdvancedWidgetComponent implements OnInit {
    site: string = '';
    org: string = 'quality'; // Default for parking-and-transfer
    currentLang: string = 'sv';
    searchData: any = null;
    discountCodeControl = new FormControl('');

    // Fields from ParkingAndTransferBookingComponent
    isArrivalHotel: boolean = false;
    isDepartureHotel: boolean = false;
    flightTimeWarning: boolean = false;

    departureFlightDate: any;
    departureFlight: any;
    departurePassengers: any;
    departureRoute: any;
    departureDatetime: any;
    departureDatetimeForTransfer: any;
    departureTerminal: any;

    arrivalDate: any;
    arrivalFlight: any;
    arrivalRoute: any;
    arrivalTerminal: any;
    parkingEndDatetime: any;
    isArrivalByFlight: boolean = true;
    parkingStartDate: any;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslocoService,
        private bookingConfigService: BookingConfigService,
        private formService: FormService,
        private routeParamService: RouteParamService,
        private datePipe: DatePipe
    ) {
        this.currentLang = this.translate.getActiveLang();
        this.bookingConfigService.setOrganization(this.org);
    }

    ngOnInit(): void {
        const config = this.bookingConfigService.getConfig();
        this.site = config.site;
        this.formService.initForm(this.site);

        const search = this.formService.getForm().get('search');
        const flights = this.formService.getForm().get('flights');
        const hotels = this.formService.getForm().get('hotels');
        const pickups = this.formService.getForm().get('pickup');
        const terminals = this.formService.getForm().get('terminal');

        // Subscribe to form changes to keep local state updated (matching logic from ParkingAndTransferBookingComponent)
        search.controls.isArrivalHotel.valueChanges.subscribe((val: any) => this.isArrivalHotel = val);
        search.controls.isDepartureHotel.valueChanges.subscribe((val: any) => this.isDepartureHotel = val);
        search.controls.isArrivalByFlight.valueChanges.subscribe((val: any) => this.isArrivalByFlight = val);

        flights.controls.departureFlight.valueChanges.subscribe((val: any) => this.departureFlight = val);
        flights.controls.arrivalFlight.valueChanges.subscribe((val: any) => this.arrivalFlight = val);

        hotels.controls.departureFlightDate.valueChanges.subscribe((val: any) => this.departureFlightDate = val);
        hotels.controls.departureDatetime.valueChanges.subscribe((val: any) => this.departureDatetime = val);
        hotels.controls.departureDatetimeForTransfer.valueChanges.subscribe((val: any) => this.departureDatetimeForTransfer = val);
        hotels.controls.arrivalDate.valueChanges.subscribe((val: any) => this.arrivalDate = val);
        hotels.controls.parkingEndDatetime.valueChanges.subscribe((val: any) => this.parkingEndDatetime = val);
        hotels.controls.parkingStartDate.valueChanges.subscribe((val: any) => this.parkingStartDate = val);

        pickups.controls.departurePassengers.valueChanges.subscribe((val: any) => this.departurePassengers = val);
        pickups.controls.departureRoute.valueChanges.subscribe((val: any) => this.departureRoute = val);
        pickups.controls.arrivalRoute.valueChanges.subscribe((val: any) => this.arrivalRoute = val);

        terminals.controls.departureTerminal.valueChanges.subscribe((val: any) => this.departureTerminal = val);
        terminals.controls.arrivalTerminal.valueChanges.subscribe((val: any) => this.arrivalTerminal = val);

        // Initialize initial values
        this.isArrivalHotel = search.controls.isArrivalHotel.value;
        this.isDepartureHotel = search.controls.isDepartureHotel.value;
        this.isArrivalByFlight = search.controls.isArrivalByFlight.value;

        this.departureFlight = flights.controls.departureFlight.value;
        this.arrivalFlight = flights.controls.arrivalFlight.value;

        this.departureFlightDate = hotels.controls.departureFlightDate.value;
        this.departureDatetime = hotels.controls.departureDatetime.value;
        this.departureDatetimeForTransfer = hotels.controls.departureDatetimeForTransfer.value;
        this.arrivalDate = hotels.controls.arrivalDate.value;
        this.parkingEndDatetime = hotels.controls.parkingEndDatetime.value;
        this.parkingStartDate = hotels.controls.parkingStartDate.value;

        this.departurePassengers = pickups.controls.departurePassengers.value;
        this.departureRoute = pickups.controls.departureRoute.value;
        this.arrivalRoute = pickups.controls.arrivalRoute.value;

        this.departureTerminal = terminals.controls.departureTerminal.value;
        this.arrivalTerminal = terminals.controls.arrivalTerminal.value;
    }

    get isParkingDurationExceeded(): boolean {
        if (this.isArrivalHotel == false && this.isDepartureHotel == false) {
            return false;
        }
        return this.checkParkingDuration();
    }

    private checkParkingDuration(): boolean {
        let parkingStartDateTime: string = '';
        let parkingEndDateTime: string = '';

        if (this.isDepartureHotel) {
            parkingStartDateTime = this.departureDatetime;
        } else {
            if (this.departureRoute?.startDate) {
                const date = this.departureRoute.startDate.toDate();
                date.setHours(date.getHours() - 1);
                parkingStartDateTime = this.formatDateTime(date);
            } else {
                return false;
            }
        }

        if (this.isArrivalHotel) {
            parkingEndDateTime = this.parkingEndDatetime;
        } else {
            if (this.arrivalRoute?.startDate) {
                const date = this.arrivalRoute.startDate.toDate();
                date.setHours(date.getHours() + 1);
                parkingEndDateTime = this.formatDateTime(date);
            } else {
                return false;
            }
        }

        if (parkingStartDateTime && parkingEndDateTime) {
            const startDate = new Date(parkingStartDateTime);
            const endDate = new Date(parkingEndDateTime);
            const diffInMs = endDate.getTime() - startDate.getTime();
            const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
            return diffInDays > 28;
        }

        return false;
    }

    get isContinueButtonEnabled(): boolean {
        if (this.isArrivalHotel == false && this.isDepartureHotel == false) {
            return false;
        }

        let isDepartureComplete = false;
        let isArrivalComplete = false;

        const option1 = this.departureFlightDate && this.departurePassengers && this.departureFlight && this.departureRoute;
        const option2 = this.departureDatetime && this.departurePassengers && this.departureDatetimeForTransfer && this.departureRoute && this.departureTerminal;
        isDepartureComplete = option1 || option2;

        let case1 = this.arrivalDate && this.departurePassengers && this.arrivalFlight && this.arrivalRoute;
        let case2 = this.arrivalDate && this.departurePassengers && this.arrivalTerminal && this.arrivalRoute;
        if (this.isArrivalHotel) {
            case1 = case1 && this.parkingEndDatetime;
            case2 = case2 && this.parkingEndDatetime;
        }
        isArrivalComplete = case1 || case2;

        if (!(isDepartureComplete && isArrivalComplete)) {
            return false;
        }

        return !this.checkParkingDuration();
    }

    goToBookingForm() {
        let parkingStartDateTime: string;
        let parkingEndDateTime: string;

        if (this.isDepartureHotel) {
            parkingStartDateTime = this.departureDatetime;
        } else {
            if (this.departureRoute?.startDate) {
                const date = this.departureRoute.startDate.toDate();
                date.setHours(date.getHours() - 1);
                parkingStartDateTime = this.formatDateTime(date);
            } else {
                parkingStartDateTime = '';
            }
        }

        if (this.isArrivalHotel) {
            parkingEndDateTime = this.parkingEndDatetime;
        } else {
            if (this.arrivalRoute?.startDate) {
                const date = this.arrivalRoute.startDate.toDate();
                date.setHours(date.getHours() + 1);
                parkingEndDateTime = this.formatDateTime(date);
            } else {
                parkingEndDateTime = '';
            }
        }

        const formattedParkingStart = this.datePipe.transform(parkingStartDateTime, 'yyyy-MM-dd HH:mm');
        const formattedParkingEnd = this.datePipe.transform(parkingEndDateTime, 'yyyy-MM-dd HH:mm');

        const serializeRoute = (route: any) => route ? {
            ...route,
            startDate: route.startDate ? (route.startDate.toISOString ? route.startDate.toISOString() : route.startDate.format('YYYY-MM-DDTHH:mm:ss')) : null,
            endDate: route.endDate ? (route.endDate.toISOString ? route.endDate.toISOString() : route.endDate.format('YYYY-MM-DDTHH:mm:ss')) : null
        } : null;

        const getValString = (val: any) => {
            if (!val) return null;
            if (val instanceof Date) return this.formatDateTime(val);
            if (typeof val === 'string') return val;
            return val;
        };

        let searchData = {
            isArrivalHotel: this.isArrivalHotel,
            isDepartureHotel: this.isDepartureHotel,
            isArrivalByFlight: this.isArrivalByFlight,
            arrivalDate: getValString(this.arrivalDate),
            arrivalFlight: this.arrivalFlight,
            departureFlight: this.departureFlight,
            parkingEndDatetime: getValString(this.parkingEndDatetime),
            departureFlightDate: getValString(this.departureFlightDate),
            departureDatetime: getValString(this.departureDatetime),
            departureDatetimeForTransfer: getValString(this.departureDatetimeForTransfer),
            departurePassengers: this.departurePassengers,
            departureRoute: serializeRoute(this.departureRoute),
            arrivalRoute: serializeRoute(this.arrivalRoute),
            arrivalTerminal: this.arrivalTerminal,
            departureTerminal: this.departureTerminal,
            parkingStartDate: this.isDepartureHotel ? '' : getValString(this.parkingStartDate)
        };

        const bookingData = {
            arrival_flight: this.arrivalFlight,
            departure_flight: this.departureFlight,
            departure_hotel: this.isDepartureHotel ? this.searchData?.departureHotel : null,
            arrival_hotel: this.isArrivalHotel ? this.searchData?.arrivalHotel : null,
            searchData,
            org: this.org || this.site
        };

        // Use localStorage instead of sessionStorage since new tabs don't share sessionStorage
        localStorage.setItem('bookingData', JSON.stringify(bookingData));

        // Build query parameters - keeping only essential URL parameters
        const queryParams: any = {
            parking_start: formattedParkingStart,
            parking_end: formattedParkingEnd,
            discount_code: this.discountCodeControl.value?.length ? this.discountCodeControl.value : 'NONE',
            booking_data: JSON.stringify(bookingData)
        };

        const urlTree = this.router.createUrlTree(
            ['parkifly/parking-and-transfer', this.site, 'booking', queryParams]
        );

        const relativeUrl = this.router.serializeUrl(urlTree);
        const fullUrl = window.location.origin + relativeUrl;
        window.open(fullUrl, '_blank');
    }

    setLanguage(lang: string) {
        this.currentLang = lang;
        this.translate.setActiveLang(lang);
    }

    private formatDateTime(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }
}
